/**
 * ChatStreamService
 *
 * Manages a chatStreamWorker instance for AI chat WebSocket streaming.
 * Mirrors the RunStreamService pattern: WebSocket lives in a Web Worker
 * (survives React unmounts / page navigation), this class handles
 * reconnection, message transformation, and handler routing.
 *
 * Two singleton instances are exported:
 *   chatStreamService     — Script Generation (WEBSOCKET_URL)
 *   analyzerStreamService — Quality Intelligence (WEBSOCKET_CHAT_URL)
 *
 * @module shared/workers/ChatStreamService
 */

import { WEBSOCKET_URL, WEBSOCKET_CHAT_URL } from '../config/conversationApiEndpoints';
import logger from '../services/logging/logger';

const MAX_RECONNECT_ATTEMPTS = 5;
const RECONNECT_DELAYS = [1000, 2000, 4000, 8000, 15000];
const GRACE_PERIOD_MS = 5000;

// ── In-flight persistence ───────────────────────────────────────
// Survives page reloads so a mid-stream refresh can resume from where it left off.
// Backend caches chunks for ~1 hour; we use a 30-minute TTL to stay well inside that.
const INFLIGHT_KEY = (conversationId) => `analyser:inflight:${conversationId}`;
const INFLIGHT_TTL_MS = 30 * 60 * 1000;

// ── Message Transformation ──────────────────────────────────────
// Normalises raw backend messages into a consistent shape for handlers.

const messageTypeHandlers = {
  stop_confirmed: (d) => ({ type: 'complete', content: '', stopped: true, chatId: d.chatId, conversationId: d.conversationId }),

  // Latest protocol (2026-05-20): type-discriminated chunks from subscriber socket
  text: (d) => d.content
    ? { type: 'stream', content: d.content, chatId: d.chatId, sequence: d.sequence }
    : null,
  end: (d) => ({ type: 'complete', content: '', chatId: d.chatId, sequence: d.sequence }),
  metadata: (d) => ({ type: 'metadata', chatId: d.chatId, data: d }),
  processing_completed: (d) => ({ type: 'processing_completed', chatId: d.chatId }),

  // Previous async streaming protocol: chunkType discriminates content vs terminal
  streaming_response: (d) => {
    if (d.chunkType === 'COMPLETE' || (!d.chunkType && d.chatStatus === 'COMPLETE')) {
      return { type: 'complete', content: d.chunk || '', chatId: d.chatId, conversationId: d.conversationId, sequence: d.sequence, chunkType: d.chunkType };
    }
    if (d.chunk) {
      return { type: 'stream', content: d.chunk, chatId: d.chatId, conversationId: d.conversationId, connectionId: d.connectionId, sequence: d.sequence, isReplay: d.isReplay || false, chunkType: d.chunkType };
    }
    return null;
  },

  processing_started: (d) => ({ type: 'processing_started', chatId: d.chatId, conversationId: d.conversationId, message: d.message }),
  subscription_confirmed: (d) => ({ type: 'subscription_confirmed', chatId: d.chatId }),
  subscriber_disconnected: (d) => ({ type: 'subscriber_disconnected', chatId: d.chatId, fromSequence: d.resumeInstructions?.fromSequence ?? d.lastChunkSequenceSent + 1 }),
  token_limit_reached: (d) => ({ type: 'token_limit_reached', chatId: d.chatId, message: d.message, outputTokens: d.outputTokens, maxTokens: d.maxTokens, timestamp: d.timestamp }),

  ai_response: (d) => ({ type: 'complete', content: d.message || '', chatId: d.chatId, conversationId: d.conversationId, connectionId: d.connectionId }),
  title_generated: (d) => ({ type: 'title_generated', content: d.conversationTitle || d.title, conversationId: d.conversationId }),
  streaming_response_error: (d) => ({ type: 'streaming_error', content: d.message || null, errorCode: d.error || null, chatId: d.chatId, conversationId: d.conversationId, timestamp: d.timestamp }),
  streaming_error: (d) => ({ type: 'streaming_error', content: d.message || null, errorCode: d.error || null, chatId: d.chatId, conversationId: d.conversationId, timestamp: d.timestamp }),
  rate_limit_exceeded: (d) => ({ type: 'rate_limit_exceeded', content: d.message || null, timestamp: d.timestamp || null, chatId: d.chatId, conversationId: d.conversationId }),
  streaming_warning: (d) => ({ type: 'streaming_warning', content: d.message || null, warningCode: d.warning || null, stallCount: d.stallCount || 1, stallDurationSeconds: d.stallDurationSeconds || 60, chatId: d.chatId, conversationId: d.conversationId }),
};

const chatStatusHandlers = {
  COMPLETE: (d) => ({ type: 'complete', content: d.chunk || '', chatId: d.chatId, conversationId: d.conversationId, connectionId: d.connectionId }),
  INPROGRESS: (d) => d.chunk ? { type: 'stream', content: d.chunk, chatId: d.chatId, conversationId: d.conversationId, connectionId: d.connectionId } : null,
  TERMINATED: (d) => ({ type: 'streaming_error', content: d.message || 'Stream terminated.', errorCode: d.error || null, chatId: d.chatId, conversationId: d.conversationId }),
  FAILED: (d) => ({ type: 'streaming_error', content: d.message || 'Stream failed.', errorCode: d.error || null, chatId: d.chatId, conversationId: d.conversationId }),
  ERROR: (d) => ({ type: 'streaming_error', content: d.message || 'Stream error.', errorCode: d.error || null, chatId: d.chatId, conversationId: d.conversationId }),
};

const transformMessage = (data) => {
  if (!data) return null;

  if (data.action === 'stopMessage') {
    return messageTypeHandlers.stop_confirmed(data);
  }

  // Drop AWS API Gateway system messages (keepalive acks, etc.)
  if (data.message && !data.chatId && !data.chatStatus && !data.type && !data.conversationId && !data.conversationTitle) {
    return null;
  }

  if (data.type) {
    const handler = messageTypeHandlers[data.type];
    if (handler) {
      const result = handler(data);
      if (result) return result;
    }
  }

  if (data.chatStatus) {
    const handler = chatStatusHandlers[data.chatStatus];
    if (handler) {
      const result = handler(data);
      if (result) return result;
      return data;
    }
    if (data.message) {
      return { type: 'streaming_error', content: data.message, chatId: data.chatId, conversationId: data.conversationId };
    }
    return data;
  }

  // chatId assigned (no type, no chatStatus)
  if (data.chatId && !data.type && !data.chatStatus) {
    return { type: 'chatId_assigned', chatId: data.chatId, conversationId: data.conversationId, connectionId: data.connectionId };
  }

  if (data.conversationTitle) {
    return messageTypeHandlers.title_generated(data);
  }

  return data;
};

// ── Service Class ───────────────────────────────────────────────

class ChatStreamService {
  constructor(url) {
    this._url = url;
    this._worker = null;
    this._generation = 0;
    this._handlers = new Map();
    this._handlerIdCounter = 0;
    this._reconnectAttempts = 0;
    this._reconnectTimer = null;
    this._refCount = 0;
    this._gracePeriodTimer = null;

    // Async streaming protocol state
    this._sessionUserId = null;       // cached from last sendMessage for auto-subscribe
    this._sessionConversationId = null;
    this._sessionTenantId = null;
    this._activeChatId = null;        // chatId of the in-flight stream
    this._sequences = new Map();      // chatId → last received sequence number

    // Stream dispatch batching — accumulate tiny content chunks before handing to React handlers.
    // Sequence tracking still runs on every individual chunk (see _handleWorkerMessage) so gap
    // detection is unaffected. Only the dispatch to UI handlers is batched.
    this._streamBatchContent = '';
    this._streamBatchBase = null;    // metadata (chatId, type, …) from the first chunk in window
    this._streamBatchTimer = null;

    // Force-close on session expiry (logout or token expiry) so the worker doesn't
    // continue running with stale credentials after the user leaves.
    if (typeof window !== 'undefined') {
      window.addEventListener('sessionExpired', () => this.forceClose());
    }
  }

  // ── Public API ────────────────────────────────────────────────

  /**
   * Increment ref count and start the worker if not already running.
   * Safe to call from multiple components — uses reference counting.
   */
  connect() {
    this._refCount++;
    if (this._gracePeriodTimer) {
      clearTimeout(this._gracePeriodTimer);
      this._gracePeriodTimer = null;
    }
    if (!this._worker) {
      this._startWorker();
    }
  }

  /**
   * Decrement ref count. After a grace period, stop the worker if nobody
   * else is still connected (allows for component remounts without disruption).
   */
  disconnect() {
    this._refCount = Math.max(0, this._refCount - 1);
    if (this._refCount === 0) {
      this._gracePeriodTimer = setTimeout(() => {
        this._gracePeriodTimer = null;
        if (this._refCount === 0) {
          this._stopWorker();
        }
      }, GRACE_PERIOD_MS);
    }
  }

  /**
   * Pre-seed session identity before the first message is sent.
   * Must be called before connect() when page-reload resumption is needed,
   * so that WS_OPEN has a conversationId to look up the in-flight entry.
   */
  setSession({ userId, conversationId, tenantId }) {
    if (userId)         this._sessionUserId = userId;
    if (conversationId) this._sessionConversationId = conversationId;
    if (tenantId)       this._sessionTenantId = tenantId;
  }

  /**
   * Generate a chatId, subscribe on the subscriber socket first, then send
   * the message on the primary socket with aiResponseChatId injected.
   *
   * Per the 2026-05-20 protocol update:
   *   - Frontend generates the chatId (crypto.randomUUID)
   *   - subscribe carries field "chatId"
   *   - publishEvent / sendMessage carries field "aiResponseChatId"
   *   - subscribe must be sent before publishEvent to avoid stream cancellation
   */
  sendMessage(payload) {
    if (payload.userId)         this._sessionUserId = payload.userId;
    if (payload.conversationId) this._sessionConversationId = payload.conversationId;
    if (payload.tenantId)       this._sessionTenantId = payload.tenantId;

    if (!this._worker) {
      logger.warn('ChatStreamService: sendMessage called but worker not running');
      return;
    }

    // Control actions (terminateEvent, stopMessage, etc.) go directly on the primary
    // socket without generating a chatId or subscribing — they reference an already-live
    // chatId supplied by the caller.
    const action = payload.action;
    if (action !== 'publishEvent' && action !== 'sendMessage') {
      this._worker.postMessage({ type: 'SEND_MESSAGE', payload });
      return;
    }

    const chatId = crypto.randomUUID();
    this._activeChatId = chatId;
    this._sequences.set(chatId, 0); // chunks start at sequence 1

    logger.info('ChatStreamService: sending — subscribe first', {
      chatId, conversationId: this._sessionConversationId
    });

    // 1. Subscribe on SUBSCRIBER socket first (field: "chatId")
    this.sendSubscribe({
      action: 'subscribe',
      chatId,
      userId: this._sessionUserId,
      conversationId: this._sessionConversationId,
      tenantId: this._sessionTenantId,
    });

    // 2. Send message on PRIMARY socket (field: "aiResponseChatId")
    this._worker.postMessage({
      type: 'SEND_MESSAGE',
      payload: { ...payload, aiResponseChatId: chatId },
    });
  }

  /**
   * Register a message handler. Returns a cleanup function.
   * The handler receives normalised message objects (type, content, chatId, …).
   */
  registerHandler(fn) {
    const id = ++this._handlerIdCounter;
    this._handlers.set(id, fn);
    return () => this._handlers.delete(id);
  }

  /**
   * Send a subscribe (or re-subscribe) action on the subscriber WebSocket connection.
   * Must be used instead of sendMessage for the subscribe handshake — the backend
   * delivers chunks ONLY to subscribers, not to the primary sender.
   */
  sendSubscribe(payload) {
    if (this._worker) {
      this._worker.postMessage({ type: 'SEND_SUBSCRIBE', payload });
    } else {
      logger.warn('ChatStreamService: sendSubscribe called but worker not running');
    }
  }

  /**
   * Check if the worker is running.
   */
  isRunning() {
    return !!this._worker;
  }

  /**
   * No-ops kept for backward compatibility with callers that previously used
   * WebSocketManager-specific methods. The worker-based design does not need
   * these signals — the worker is isolated and survives navigation naturally.
   */
  setBackgroundHold() {}
  setGenerationActive() {}
  notifyGenerationComplete() {}
  setStreamingActive() {}
  clearPendingMessages() {}
  updateActivity() {}

  /**
   * Reset the sequence counter for the active chat so the next re-subscribe
   * requests fromSequence=1, causing the backend to replay the full response
   * from the beginning. Call this before wiping message content on reconnect
   * so the replayed stream rebuilds complete artifact tags.
   */
  resetSequenceForFullReplay() {
    if (this._activeChatId) {
      this._sequences.set(this._activeChatId, 0);
    }
  }

  /**
   * Attempt to resume an in-flight stream after a connection failure.
   * Re-establishes the WebSocket and re-subscribes from the last received
   * sequence so the backend sends only the remaining chunks, not the full response.
   * Returns true if there is an active chatId to resume, false if the stream is gone.
   */
  retrySubscription() {
    if (!this._activeChatId || !this._sessionUserId) return false;
    this._reconnectAttempts = 0;
    if (!this._worker) {
      // Worker was terminated after max attempts — restart it.
      // WS_OPEN will automatically re-subscribe using _activeChatId and _sequences.
      this._startWorker();
    } else {
      // Worker still alive — re-subscribe directly from last received sequence.
      const fromSequence = (this._sequences.get(this._activeChatId) || 0) + 1;
      this.sendSubscribe({
        action: 'subscribe',
        chatId: this._activeChatId,
        userId: this._sessionUserId,
        conversationId: this._sessionConversationId,
        tenantId: this._sessionTenantId,
        fromSequence
      });
    }
    return true;
  }

  /**
   * Returns true if there is an in-flight stream that can potentially be resumed.
   */
  isStreamActive() {
    return !!this._activeChatId;
  }

  /**
   * Close immediately without the grace period.
   * Use when the user explicitly leaves generation (stop / navigate away).
   */
  forceClose() {
    this._clearReconnectTimer();
    if (this._gracePeriodTimer) {
      clearTimeout(this._gracePeriodTimer);
      this._gracePeriodTimer = null;
    }
    this._refCount = 0;
    this._stopWorker();
  }

  // ── Worker Lifecycle ──────────────────────────────────────────

  _startWorker() {
    this._generation++;
    const gen = this._generation;

    this._worker = new Worker(
      new URL('./chatStreamWorker.js', import.meta.url)
    );

    this._worker.onmessage = (event) => {
      if (gen !== this._generation) return;
      this._handleWorkerMessage(event);
    };

    this._worker.onerror = (err) => {
      if (gen !== this._generation) return;
      logger.error('ChatStreamService: worker error', { url: this._url, error: err.message });
      this._destroyWorker();
      if (this._refCount > 0) this._scheduleReconnect();
    };

    this._worker.postMessage({ type: 'CONNECT', url: this._url });
  }

  _destroyWorker() {
    if (this._worker) {
      this._worker.onmessage = null;
      this._worker.onerror = null;
      this._worker.terminate();
      this._worker = null;
    }
  }

  _stopWorker() {
    this._clearReconnectTimer();
    this._flushStreamBatch();
    this._clearInflight();
    this._activeChatId = null;
    this._sequences.clear();
    // Clear cached session data so a reconnect after re-login can't use stale credentials
    this._sessionUserId = null;
    this._sessionConversationId = null;
    this._sessionTenantId = null;
    if (this._worker) {
      try { this._worker.postMessage({ type: 'DISCONNECT' }); } catch {}
      this._destroyWorker();
    }
    this._reconnectAttempts = 0;
    this._refCount = 0;
  }

  _clearInflight() {
    if (this._sessionConversationId) {
      try { localStorage.removeItem(INFLIGHT_KEY(this._sessionConversationId)); } catch {}
    }
  }

  // ── Message Routing ───────────────────────────────────────────

  _handleWorkerMessage(event) {
    const { type, rawMessage, message, code } = event.data;

    switch (type) {
      case 'WS_OPEN':
        logger.info('ChatStreamService: PRIMARY connected', { url: this._url, subState: event.data.subState, activeChatId: this._activeChatId });
        if (this._reconnectAttempts > 0) {
          // Only dispatch 'reconnected' when this open follows a reconnect attempt (not the initial connect)
          this._dispatch({ type: 'connection_status', status: 'reconnected' });
        }
        this._reconnectAttempts = 0;

        if (this._activeChatId && this._sessionUserId) {
          // In-memory reconnect (network blip, worker restart) — re-subscribe from
          // where we left off so already-received chunks are not replayed.
          const fromSequence = (this._sequences.get(this._activeChatId) || 0) + 1;
          logger.info('ChatStreamService: reconnected — re-subscribing in-flight chat', {
            chatId: this._activeChatId, fromSequence
          });
          this.sendSubscribe({
            action: 'subscribe',
            chatId: this._activeChatId,
            userId: this._sessionUserId,
            conversationId: this._sessionConversationId,
            tenantId: this._sessionTenantId,
            fromSequence
          });
        } else if (this._sessionConversationId) {
          // Page-reload resumption — check localStorage for a chat that was in-flight
          // when the page closed. Backend caches chunks for ~1 hour; TTL is 30 min.
          try {
            const raw = localStorage.getItem(INFLIGHT_KEY(this._sessionConversationId));
            if (raw) {
              const { chatId, lastSequence, userId, tenantId, conversationId, updatedAt } = JSON.parse(raw);
              if (Date.now() - updatedAt > INFLIGHT_TTL_MS) {
                logger.info('ChatStreamService: stale in-flight entry — discarding', { chatId, conversationId });
                localStorage.removeItem(INFLIGHT_KEY(conversationId));
              } else {
                const fromSequence = lastSequence + 1;
                logger.info('ChatStreamService: resuming in-flight chat after reload', { chatId, fromSequence, conversationId });
                this._activeChatId = chatId;
                this._sequences.set(chatId, lastSequence);
                this.sendSubscribe({
                  action: 'subscribe',
                  chatId,
                  userId,
                  conversationId,
                  tenantId,
                  fromSequence,
                });
              }
            }
          } catch (e) {
            logger.warn('ChatStreamService: could not read in-flight entry from storage', e);
          }
        }
        break;

      case 'WS_CHUNK':
      case 'WS_MESSAGE': {
        const transformed = transformMessage(rawMessage);
        if (!transformed) break;

        // Token limit hit — stream is complete but truncated. Clear active chat and notify UI.
        if (transformed.type === 'token_limit_reached') {
          logger.warn('ChatStreamService: token_limit_reached', { chatId: transformed.chatId, outputTokens: transformed.outputTokens, maxTokens: transformed.maxTokens });
          if (transformed.chatId === this._activeChatId) {
            this._activeChatId = null;
            this._sequences.delete(transformed.chatId);
          }
          this._dispatch(transformed);
          break;
        }

        // Subscriber dropped mid-stream — re-subscribe from the sequence the backend cached.
        if (transformed.type === 'subscriber_disconnected') {
          const { chatId, fromSequence } = transformed;
          logger.info('ChatStreamService: subscriber_disconnected — re-subscribing', { chatId, fromSequence });
          if (this._sessionUserId && this._sessionConversationId) {
            this.sendSubscribe({
              action: 'subscribe',
              chatId,
              userId: this._sessionUserId,
              conversationId: this._sessionConversationId,
              tenantId: this._sessionTenantId,
              fromSequence,
            });
          }
          // Don't dispatch to UI handlers — this is an internal recovery event
          break;
        }

        // processing_started: chatId was generated by us — just log and forward for UI feedback
        if (transformed.type === 'processing_started') {
          logger.info('ChatStreamService: processing_started', {
            chatId: transformed.chatId, activeChatId: this._activeChatId
          });
        }

        // Sequence tracking and gap detection for streaming_response chunks
        if (transformed.type === 'stream' && rawMessage.sequence != null) {
          const chatId = transformed.chatId;
          const lastSeq = this._sequences.get(chatId) || 0;
          const received = rawMessage.sequence;

          // Detect gaps (skip the very first chunk where lastSeq is 0 and seq may be 1)
          if (lastSeq > 0 && received > lastSeq + 1) {
            logger.warn('ChatStreamService: sequence gap detected — re-subscribing', {
              chatId, expected: lastSeq + 1, received
            });
            if (this._sessionUserId && this._sessionConversationId) {
              this.sendSubscribe({
                action: 'subscribe',
                chatId,
                userId: this._sessionUserId,
                conversationId: this._sessionConversationId,
                tenantId: this._sessionTenantId,
                fromSequence: lastSeq + 1
              });
            }
          }
          // Only advance — never rewind. Replay chunks (sequence < lastSeq)
          // must not reset the high-water mark or the next live chunk will
          // be misread as a fresh gap and trigger another re-subscribe.
          if (received > lastSeq) {
            this._sequences.set(chatId, received);
          }

          // Persist in-flight state so a page reload can resume this stream.
          if (this._sessionConversationId) {
            try {
              localStorage.setItem(INFLIGHT_KEY(this._sessionConversationId), JSON.stringify({
                chatId,
                lastSequence: this._sequences.get(chatId),
                userId: this._sessionUserId,
                tenantId: this._sessionTenantId,
                conversationId: this._sessionConversationId,
                updatedAt: Date.now(),
              }));
            } catch { /* localStorage unavailable (private browsing quota etc.) — non-fatal */ }
          }
        }

        // Clear active chat on all terminal events so state never leaks into the
        // next conversation. Covers: normal completion, user stop (stop_confirmed
        // maps to complete+stopped), streaming errors, and rate limiting.
        const isTerminal = (
          transformed.type === 'complete' ||
          transformed.type === 'streaming_error' ||
          transformed.type === 'rate_limit_exceeded'
        );
        if (isTerminal && transformed.chatId === this._activeChatId) {
          this._activeChatId = null;
          this._sequences.delete(transformed.chatId);
          this._clearInflight();
        }

        if (transformed.type === 'stream') {
          // Batch tiny content chunks — sequence tracking already ran above so gap detection
          // is unaffected. Leading edge dispatches immediately; subsequent chunks within the
          // 100ms window are merged and flushed as one, reducing React render frequency.
          this._dispatchStream(transformed);
        } else {
          // Non-stream events (complete, error, metadata, etc.): flush any pending batch first
          // so accumulated content always arrives before the terminal event.
          this._flushStreamBatch();
          this._dispatch(transformed);
        }
        break;
      }

      case 'WS_ERROR':
        logger.error('ChatStreamService: PRIMARY WebSocket error', { url: this._url, message });
        this._destroyWorker();
        if (this._refCount > 0) this._scheduleReconnect();
        break;

      case 'WS_CLOSED':
        logger.info('ChatStreamService: PRIMARY WebSocket closed', { url: this._url, code });
        this._destroyWorker();
        if (code !== 1000 && this._refCount > 0) this._scheduleReconnect();
        break;

      case 'WS_DIAG': {
        const { event: diagEvent, ...diagData } = event.data;
        const logFn = diagEvent === 'SUBSCRIBE_DROPPED' || diagEvent === 'SUB_ERROR' ? 'error' : 'info';
        logger[logFn](`ChatStreamService [WS]: ${diagEvent}`, { ...diagData, activeChatId: this._activeChatId, lastSeq: diagData.chatId ? this._sequences.get(diagData.chatId) : undefined });
        break;
      }

      default:
        break;
    }
  }

  _dispatchStream(msg) {
    if (!this._streamBatchTimer) {
      // Leading edge: send the first chunk immediately so the UI starts updating
      // without delay, then open a 100ms window to accumulate the rest.
      this._dispatch(msg);
      this._streamBatchContent = '';
      this._streamBatchBase = msg;
      this._streamBatchTimer = setTimeout(() => {
        if (this._streamBatchContent) {
          this._dispatch({ ...this._streamBatchBase, content: this._streamBatchContent });
          this._streamBatchContent = '';
        }
        this._streamBatchBase = null;
        this._streamBatchTimer = null;
      }, 100);
    } else {
      // Within window: accumulate content only — no dispatch, no React render.
      this._streamBatchContent += msg.content || '';
    }
  }

  _flushStreamBatch() {
    if (!this._streamBatchTimer) return;
    clearTimeout(this._streamBatchTimer);
    this._streamBatchTimer = null;
    if (this._streamBatchContent) {
      this._dispatch({ ...this._streamBatchBase, content: this._streamBatchContent });
      this._streamBatchContent = '';
    }
    this._streamBatchBase = null;
  }

  _dispatch(msg) {
    this._handlers.forEach((fn) => {
      try { fn(msg); } catch (err) {
        logger.error('ChatStreamService: handler error', { error: err.message });
      }
    });
  }

  // ── Reconnection ──────────────────────────────────────────────

  _scheduleReconnect() {
    if (this._reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
      logger.error('ChatStreamService: max reconnect attempts reached', { url: this._url });
      this._dispatch({ type: 'connection_status', status: 'failed', attempt: this._reconnectAttempts });
      return;
    }

    const delay = RECONNECT_DELAYS[this._reconnectAttempts] || 15000;
    this._reconnectAttempts++;

    logger.info('ChatStreamService: scheduling reconnect', { attempt: this._reconnectAttempts, delay, url: this._url });
    this._dispatch({ type: 'connection_status', status: 'reconnecting', attempt: this._reconnectAttempts });

    this._reconnectTimer = setTimeout(() => {
      this._reconnectTimer = null;
      if (this._refCount > 0) this._startWorker();
    }, delay);
  }

  _clearReconnectTimer() {
    if (this._reconnectTimer) {
      clearTimeout(this._reconnectTimer);
      this._reconnectTimer = null;
    }
  }
}

// ── Singleton Instances ─────────────────────────────────────────

/** Script Generation — connects to WEBSOCKET_URL */
export const chatStreamService = new ChatStreamService(WEBSOCKET_URL);

/** Quality Intelligence — connects to WEBSOCKET_CHAT_URL */
export const analyzerStreamService = new ChatStreamService(WEBSOCKET_CHAT_URL);
